export const STACK_MODES = {
  Stacked: 'stacked',
  SideBySide: 'side_by_side'
}

export const STACK_MODE_LABELS = {
  [STACK_MODES.Stacked]: 'Stacked',
  [STACK_MODES.SideBySide]: 'Side-by-Side'
}

export const STRUCTURE_COMPONENT_TYPES = {
  Emc: 'emc',
  Cabinet: 'cabinet'
}

export const STRUCTURE_COMPONENT_TYPE_LABELS = {
  [STRUCTURE_COMPONENT_TYPES.Emc]: 'EMC',
  [STRUCTURE_COMPONENT_TYPES.Cabinet]: 'ID Cabinet'
}

export const toNumber = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export const roundTo = (value, places = 4) => {
  const factor = 10 ** places
  return Math.round((toNumber(value) + Number.EPSILON) * factor) / factor
}

export const inchesToFeet = (inches) => roundTo(toNumber(inches) / 12)

export const isTruthyFlag = (value) => (
  value === true || value === 1 || value === '1' || value === 'true'
)

export const isStacked = (mode) => {
  if (mode === false || mode === 0 || mode === '0') return false
  return mode !== STACK_MODES.SideBySide
}

export const normalizeStackMode = (mode) => (
  isStacked(mode) ? STACK_MODES.Stacked : STACK_MODES.SideBySide
)

export const parseStructureComponents = (value) => {
  if (Array.isArray(value)) return value
  if (!value) return []
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    return []
  }
}

export const stringifyStructureComponents = (value) => {
  if (Array.isArray(value)) return JSON.stringify(value)
  if (typeof value === 'string') {
    if (!value.trim()) return '[]'
    try {
      const parsed = JSON.parse(value)
      return JSON.stringify(Array.isArray(parsed) ? parsed : [])
    } catch (err) {
      return '[]'
    }
  }
  return '[]'
}

export const nextStructureComponentId = (components = []) => {
  const maxId = parseStructureComponents(components).reduce((max, item) => {
    const id = toNumber(item?.id)
    return id > max ? id : max
  }, 0)
  return maxId + 1
}

export const createStructureComponent = (type, components = []) => {
  const base = {
    id: nextStructureComponentId(components),
    type,
    stackMode: STACK_MODES.Stacked
  }
  if (type === STRUCTURE_COMPONENT_TYPES.Emc) {
    return { ...base, modsH: 0, modsL: 0 }
  }
  return { ...base, heightIn: 0, lengthIn: 0 }
}

export const parseFtInToken = (token) => {
  const s = String(token || '').trim()
  if (!s) return 0
  const match = s.match(/(\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?/)
  if (match) {
    return roundTo(toNumber(match[1]) + inchesToFeet(match[2]))
  }
  const n = parseFloat(s.replace(/["']/g, ''))
  return Number.isFinite(n) ? n : 0
}

export const parseNominalSize = (nominalSize) => {
  if (!nominalSize) return { heightFt: 0, lengthFt: 0 }
  const parts = String(nominalSize).split(/\s*x\s*/i)
  return {
    heightFt: parseFtInToken(parts[0]),
    lengthFt: parseFtInToken(parts[1])
  }
}

export const getAutoGroundClearanceFt = ({ noPedestal, pedestalHeightIn, pedestalNominalSize } = {}) => {
  if (isTruthyFlag(noPedestal)) return 0
  if (toNumber(pedestalHeightIn)) return roundTo(inchesToFeet(pedestalHeightIn), 2)
  return roundTo(parseNominalSize(pedestalNominalSize).heightFt, 2)
}

export const getEmcHeightFt = ({ noEmc, emcModsH, emcHeightFt, emcNominalSize } = {}) => {
  if (isTruthyFlag(noEmc)) return 0
  const stored = toNumber(emcHeightFt || emcModsH)
  if (stored) return stored
  return parseNominalSize(emcNominalSize).heightFt
}

export const getEmcLengthFt = ({ noEmc, emcModsL, emcLengthFt, emcNominalSize } = {}) => {
  if (isTruthyFlag(noEmc)) return 0
  const stored = toNumber(emcLengthFt || emcModsL)
  if (stored) return stored
  return parseNominalSize(emcNominalSize).lengthFt
}

export const getCabinetHeightFt = ({ noCabinet, cabinetHeightFt, cabinetHeightIn, cabinetNominalSize } = {}) => {
  if (isTruthyFlag(noCabinet)) return 0
  const fromParts = roundTo(toNumber(cabinetHeightFt) + inchesToFeet(cabinetHeightIn))
  if (fromParts) return fromParts
  return parseNominalSize(cabinetNominalSize).heightFt
}

export const getCabinetLengthFt = ({ noCabinet, cabinetLengthFt, cabinetLengthIn, cabinetNominalSize } = {}) => {
  if (isTruthyFlag(noCabinet)) return 0
  const fromParts = roundTo(toNumber(cabinetLengthFt) + inchesToFeet(cabinetLengthIn))
  if (fromParts) return fromParts
  return parseNominalSize(cabinetNominalSize).lengthFt
}

export const extraComponentHeightFt = (component = {}) => {
  if (component.type === STRUCTURE_COMPONENT_TYPES.Emc) {
    return toNumber(component.modsH || component.heightFt)
  }
  return roundTo(toNumber(component.heightFt) + inchesToFeet(component.heightIn))
}

export const extraComponentLengthFt = (component = {}) => {
  if (component.type === STRUCTURE_COMPONENT_TYPES.Emc) {
    return toNumber(component.modsL || component.lengthFt)
  }
  return roundTo(toNumber(component.lengthFt) + inchesToFeet(component.lengthIn))
}

export const extraComponentSqft = (component = {}) => (
  roundTo(extraComponentHeightFt(component) * extraComponentLengthFt(component), 2)
)

export const calculateOverallHeightFt = (input = {}) => {
  let sum = toNumber(input.groundClearanceFt)
  if (isStacked(input.emcStackMode)) {
    sum += getEmcHeightFt(input)
  }
  if (isStacked(input.cabinetStackMode)) {
    sum += getCabinetHeightFt(input)
  }
  parseStructureComponents(input.structureComponents).forEach((component) => {
    if (isStacked(component.stackMode ?? component.stacked)) {
      sum += extraComponentHeightFt(component)
    }
  })
  return roundTo(sum, 2)
}

export const calculateSignSquareFootage = (input = {}) => {
  let sqft = getEmcHeightFt(input) * getEmcLengthFt(input)
  sqft += getCabinetHeightFt(input) * getCabinetLengthFt(input)
  parseStructureComponents(input.structureComponents).forEach((component) => {
    sqft += extraComponentHeightFt(component) * extraComponentLengthFt(component)
  })
  return roundTo(sqft, 2)
}

export const formatFeetInches = (feet) => {
  const totalInches = Math.round(toNumber(feet) * 12)
  const sign = totalInches < 0 ? '-' : ''
  const abs = Math.abs(totalInches)
  const ft = Math.floor(abs / 12)
  const inches = abs % 12
  return `${sign}${ft}'-${inches}"`
}

export const formatSquareFootage = (sqft) => `${roundTo(sqft, 2).toFixed(2)} sq ft`

export const getHeightBreakdown = (input = {}) => {
  const rows = []
  const groundClearanceFt = toNumber(input.groundClearanceFt)
  rows.push({
    key: 'groundClearance',
    label: 'Ground Clearance',
    included: true,
    heightFt: groundClearanceFt
  })

  if (!isTruthyFlag(input.noEmc) && getEmcHeightFt(input) > 0) {
    const stacked = isStacked(input.emcStackMode)
    rows.push({
      key: 'emc',
      label: 'EMC',
      included: stacked,
      heightFt: getEmcHeightFt(input),
      stackMode: normalizeStackMode(input.emcStackMode)
    })
  }

  if (!isTruthyFlag(input.noCabinet) && getCabinetHeightFt(input) > 0) {
    const stacked = isStacked(input.cabinetStackMode)
    rows.push({
      key: 'cabinet',
      label: 'ID Cabinet',
      included: stacked,
      heightFt: getCabinetHeightFt(input),
      stackMode: normalizeStackMode(input.cabinetStackMode)
    })
  }

  const extraTypeCounts = {}
  parseStructureComponents(input.structureComponents).forEach((component) => {
    const heightFt = extraComponentHeightFt(component)
    if (!heightFt) return
    const stacked = isStacked(component.stackMode ?? component.stacked)
    const typeLabel = STRUCTURE_COMPONENT_TYPE_LABELS[component.type] || 'Component'
    extraTypeCounts[component.type] = (extraTypeCounts[component.type] || 1) + 1
    rows.push({
      key: `extra-${component.type}-${component.id || extraTypeCounts[component.type]}`,
      label: `${typeLabel} ${extraTypeCounts[component.type]}`,
      included: stacked,
      heightFt,
      stackMode: normalizeStackMode(component.stackMode ?? component.stacked)
    })
  })

  return rows
}

export const syncStructureFields = (input = {}) => {
  const calculatedGroundClearanceFt = getAutoGroundClearanceFt(input)
  const groundClearanceFt = isTruthyFlag(input.groundClearanceManual)
    ? toNumber(input.groundClearanceFt)
    : calculatedGroundClearanceFt
  const calculatedOverallHeightFt = calculateOverallHeightFt({ ...input, groundClearanceFt })
  const calculatedSquareFootage = calculateSignSquareFootage(input)

  return {
    groundClearanceFt,
    overallHeightFt: isTruthyFlag(input.overallHeightManual)
      ? toNumber(input.overallHeightFt)
      : calculatedOverallHeightFt,
    squareFootage: isTruthyFlag(input.squareFootageManual)
      ? toNumber(input.squareFootage)
      : calculatedSquareFootage,
    calculatedGroundClearanceFt,
    calculatedOverallHeightFt,
    calculatedSquareFootage
  }
}

export const applySyncedStructureFields = (draft) => {
  const synced = syncStructureFields(draft)
  if (toNumber(draft.groundClearanceFt) !== synced.groundClearanceFt) {
    draft.groundClearanceFt = synced.groundClearanceFt
  }
  if (toNumber(draft.overallHeightFt) !== synced.overallHeightFt) {
    draft.overallHeightFt = synced.overallHeightFt
  }
  if (toNumber(draft.squareFootage) !== synced.squareFootage) {
    draft.squareFootage = synced.squareFootage
  }
  return synced
}

export const defaultStructureFields = () => ({
  emcStackMode: STACK_MODES.Stacked,
  cabinetStackMode: STACK_MODES.Stacked,
  structureComponents: [],
  groundClearanceFt: 0,
  groundClearanceManual: false,
  overallHeightFt: 0,
  overallHeightManual: false,
  squareFootage: 0,
  squareFootageManual: false
})
